import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, AlertTriangle, Shield, FileText, Calendar, User, TrendingUp, PieChart, BarChart3 } from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  critical_risk: '#DC2626',
  imminent_danger: '#991B1B',
  high_risk: '#EA580C',
  moderate_risk: '#F59E0B',
  low_risk: '#10B981',
  informational: '#3B82F6'
};

const AnalysisDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalysis();
  }, [id]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/safety/analyses`);
      const analyses = response.data || [];
      const foundAnalysis = analyses.find(a => a.id === id);
      
      if (foundAnalysis) {
        setAnalysis(foundAnalysis);
      } else {
        toast({
          title: "Error",
          description: "Analysis not found",
          variant: "destructive"
        });
        navigate('/safety-analysis');
      }
    } catch (error) {
      console.error('Error loading analysis:', error);
      toast({
        title: "Error",
        description: "Failed to load analysis",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical_risk': 'bg-red-100 text-red-800',
      'imminent_danger': 'bg-red-200 text-red-900',
      'high_risk': 'bg-orange-100 text-orange-800',
      'moderate_risk': 'bg-yellow-100 text-yellow-800',
      'low_risk': 'bg-green-100 text-green-800',
      'informational': 'bg-blue-100 text-blue-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getRiskScoreColor = (score) => {
    if (score >= 0.8) return 'text-red-600';
    if (score >= 0.6) return 'text-orange-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-green-600';
  };

  const prepareChartData = () => {
    if (!analysis || !analysis.detected_violations) return null;

    // Severity Distribution
    const severityCount = {};
    analysis.detected_violations.forEach(v => {
      const severity = v.severity || 'unknown';
      severityCount[severity] = (severityCount[severity] || 0) + 1;
    });

    const severityData = Object.entries(severityCount).map(([severity, count]) => ({
      name: severity.replace(/_/g, ' ').toUpperCase(),
      value: count,
      color: COLORS[severity] || '#6B7280'
    }));

    // Violation Categories
    const categoryCount = {};
    analysis.detected_violations.forEach(v => {
      const type = v.violation_type?.split(' - ')[0] || 'Other';
      categoryCount[type] = (categoryCount[type] || 0) + 1;
    });

    const categoryData = Object.entries(categoryCount).map(([category, count]) => ({
      category,
      count
    }));

    // PPE Analysis Data
    const ppeData = analysis.ppe_analysis ? 
      Object.entries(analysis.ppe_analysis).map(([item, data]) => ({
        item: item.replace(/_/g, ' ').toUpperCase(),
        compliance: data.compliance ? 100 : 0,
        required: data.required ? 100 : 0
      })) : [];

    // Risk Assessment Radar
    const radarData = [
      { category: 'PPE Compliance', score: analysis.ppe_analysis ? 
        (Object.values(analysis.ppe_analysis).filter(p => p.compliance).length / 
         Object.values(analysis.ppe_analysis).length * 100) : 50 },
      { category: 'Equipment Safety', score: analysis.equipment_analysis ? 
        (Object.values(analysis.equipment_analysis).filter(e => e.compliant).length / 
         Object.values(analysis.equipment_analysis).length * 100) : 50 },
      { category: 'Overall Compliance', score: analysis.compliance_percentage || 50 },
      { category: 'Hazard Level', score: 100 - (analysis.overall_risk_score * 100) },
      { category: 'Critical Issues', score: 100 - ((analysis.critical_violations || 0) / 
        (analysis.violations_detected || 1) * 100) }
    ];

    return { severityData, categoryData, ppeData, radarData };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Analysis not found</h3>
        <Button onClick={() => navigate('/safety-analysis')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Safety Analysis
        </Button>
      </div>
    );
  }

  const chartData = prepareChartData();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button onClick={() => navigate('/safety-analysis')} variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Safety Analysis Report</h1>
            <p className="text-gray-600">{analysis.file_name}</p>
          </div>
        </div>
        <Badge className={getRiskScoreColor(analysis.overall_risk_score)}>
          Risk Score: {(analysis.overall_risk_score * 100).toFixed(0)}%
        </Badge>
      </div>

      {/* Image and Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Image Card */}
        {analysis.image_thumbnail && (
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-sm">Analyzed Image</CardTitle>
            </CardHeader>
            <CardContent>
              <img 
                src={analysis.image_thumbnail} 
                alt={analysis.file_name}
                className="w-full rounded-lg shadow-lg"
              />
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date(analysis.analysis_timestamp).toLocaleDateString()}
                </div>
                <div className="flex items-center text-gray-600">
                  <FileText className="w-4 h-4 mr-2" />
                  {(analysis.file_size / 1024).toFixed(1)} KB
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        <Card className={analysis.image_thumbnail ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="w-5 h-5 mr-2 text-blue-600" />
              Analysis Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 font-medium">Compliance Rate</p>
                <p className="text-2xl font-bold text-blue-900">
                  {analysis.compliance_percentage?.toFixed(1)}%
                </p>
                <Progress value={analysis.compliance_percentage} className="mt-2" />
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-600 font-medium">Total Violations</p>
                <p className="text-2xl font-bold text-orange-900">
                  {analysis.violations_detected || analysis.detected_violations?.length || 0}
                </p>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-red-600 font-medium">Critical Issues</p>
                <p className="text-2xl font-bold text-red-900">
                  {analysis.critical_violations || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visual Charts Section */}
      {chartData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Severity Distribution Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <PieChart className="w-5 h-5 mr-2 text-blue-600" />
                Violation Severity Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPie>
                  <Pie
                    data={chartData.severityData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.severityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Violation Categories Bar Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-orange-600" />
                Violation Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.categoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#F59E0B" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* PPE Compliance Chart */}
          {chartData.ppeData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-green-600" />
                  PPE Compliance Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.ppeData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="item" type="category" width={120} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="compliance" fill="#10B981" name="Compliant" />
                    <Bar dataKey="required" fill="#EF4444" name="Required" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Risk Assessment Radar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-purple-600" />
                Multi-Factor Risk Assessment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={chartData.radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="category" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar name="Safety Score" dataKey="score" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detailed Violations */}
      {analysis.detected_violations && analysis.detected_violations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="w-5 h-5 mr-2 text-red-600" />
              Detailed Violations & Corrective Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analysis.detected_violations.map((violation, index) => (
                <Card key={index} className="border-l-4" style={{ borderLeftColor: COLORS[violation.severity] || '#6B7280' }}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center">
                        <AlertTriangle className="w-5 h-5 mr-2" style={{ color: COLORS[violation.severity] }} />
                        <h4 className="font-semibold text-gray-900">{violation.violation_type}</h4>
                      </div>
                      <Badge className={getSeverityColor(violation.severity)}>
                        {violation.severity.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-gray-700 mb-2">{violation.description}</p>
                    
                    {violation.location_description && (
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Location:</strong> {violation.location_description}
                      </p>
                    )}
                    
                    <div className="flex items-center text-sm text-blue-600 mb-2">
                      <FileText className="w-4 h-4 mr-1" />
                      {violation.osha_reference}
                    </div>
                    
                    {violation.immediate_action_required && (
                      <Badge variant="destructive" className="mb-2">
                        Immediate Action Required
                      </Badge>
                    )}
                    
                    {violation.corrective_actions && violation.corrective_actions.length > 0 && (
                      <div className="mt-3 bg-green-50 p-3 rounded">
                        <p className="text-sm font-medium text-green-800 mb-1">Corrective Actions:</p>
                        <ul className="text-sm text-green-700 list-disc list-inside space-y-1">
                          {violation.corrective_actions.map((action, i) => (
                            <li key={i}>{action}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalysisDetailPage;
